import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * Arañita (models/spiderling.ts). Everything quick and jerky, as small spiders move: idle twitches leg by leg and
 * jerks round in little turns, walk is a fast skitter (the weaver's tetrapod at double speed, the body bouncing),
 * attack comes in reared and lunges with a bite at ~0.13 s, hit, stun (loop) and die (it flips onto its back and
 * the legs curl up).
 *
 * Conventions as the weaver's: +X on the body pitches the nose down; each leg's local +Z runs out along it, −X on
 * `leg<i>` lifts it, a Y turn swings it, +X on `knee<i>` folds it in. Legs 0–3 right, front to back; 4–7 left.
 */

const LEGS = [0, 1, 2, 3, 4, 5, 6, 7] as const;
type L = (typeof LEGS)[number];
type B = 'root' | 'body' | 'abdomen' | `leg${L}` | `knee${L}`;
type P = KeyPose<B>;

const legN = (i: number): B => `leg${i}` as B;
const kneeN = (i: number): B => `knee${i}` as B;
const side = (i: number): number => (i < 4 ? 1 : -1);
const row = (i: number): number => i % 4;
const tetra = (i: number): number => (i === 0 || i === 2 || i === 5 || i === 7 ? 0 : 1);

const legCh = (i: number, lift: number, stride: number, splay = 0, curl = 0): [[number, number, number], [number, number, number]] => [
  [-0.3 * lift + 0.2 * splay + 1.5 * curl, -side(i) * stride * 0.32, 0],
  [0.2 * lift - 0.3 * splay + 1.8 * curl, 0, 0],
];

const leg = (b: Bones<B>, i: number, lift: number, stride: number, splay = 0, curl = 0): void => {
  const [h, k] = legCh(i, lift, stride, splay, curl);
  b[legN(i)].rot(...h);
  b[kneeN(i)].rot(...k);
};

const legsPose = (fn: (i: number) => readonly [number, number, number?, number?], rest: P = {}): P => {
  const p: Record<string, [number, number, number]> = {};
  for (const i of LEGS) {
    const [lift, stride, splay = 0, curl = 0] = fn(i);
    const [h, k] = legCh(i, lift, stride, splay, curl);
    p[legN(i)] = h;
    p[kneeN(i)] = k;
  }
  return { ...(p as P), ...rest };
};

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

const ST: P = legsPose(() => [0, 0, 0.1], { root: [0, 0, 0, 0, -0.006, 0], abdomen: [0.05, 0, 0] });

/** Never quite still: legs twitch one by one, it jerks round a little, holds, jerks back; the abdomen throbs. */
const idle = (): Clip<B> =>
  clip<B>('idle', 2.4, true, (b, t) => {
    const ph = phase(t, 2.4);
    apply(b, ST);
    const turn = span(ph, 0.2, 0.24, outExpo) - span(ph, 0.62, 0.66, outExpo);
    b.root.rot(0, 0.25 * turn, 0);
    b.body.rot(0.04 * Math.sin(ph * TAU * 2), 0, 0);
    b.abdomen.scale(1 + 0.04 * Math.sin(ph * TAU * 4)).rot(0.04 * Math.sin(ph * TAU * 2 + 1));
    for (const i of LEGS) {
      const d = (((ph * 2 - ((i * 3) % 8) / 8) % 1) + 1) % 1;
      const k = d < 0.08 ? Math.sin((d / 0.08) * Math.PI) : 0;
      leg(b, i, 0.9 * k, 0.3 * k, 0);
    }
  });

/** The skitter: the tetrapod at double speed, short quick steps, the body bouncing and wobbling. */
const walk = (dur = 0.42): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const ph = phase(t, dur);
    const th = ph * TAU;
    apply(b, ST, 0.8);
    for (const i of LEGS) {
      const u = (ph + (tetra(i) ? 0.5 : 0) - row(i) * 0.04 + 1) % 1;
      const swing = u >= 0.5;
      const k = swing ? (u - 0.5) / 0.5 : u / 0.5;
      const stride = swing ? -Math.cos(Math.PI * k) : Math.cos(Math.PI * k);
      const lift = swing ? Math.sin(Math.PI * k) : 0;
      leg(b, i, 1.3 * lift, 1.1 * stride, 0.05);
    }
    b.root.pos(0, 0.012 * Math.abs(Math.sin(2 * th)) - 0.006, 0);
    b.body.rot(0.04 * Math.sin(2 * th), 0.06 * Math.sin(th), 0.05 * Math.sin(th + 0.5));
    b.abdomen.rot(0.06 * Math.cos(2 * th - 1.2), -0.1 * Math.sin(th - 1), 0);
  });

/** Reared on its back legs, front legs up; at 0.13 s it lunges and bites, then hops back. */
const COCK: P = legsPose((i) => (row(i) === 0 ? [2.6, 0.8, 0] : row(i) === 1 ? [1.2, 0.4, 0] : [0, -0.3, 0.1]), {
  root: [0, 0, 0, 0, 0.03, -0.05], body: [-0.45, 0, 0], abdomen: [0.35, 0, 0],
});
const BITE: P = legsPose((i) => (row(i) === 0 ? [0.3, 1.2, 0, 0.25] : row(i) === 1 ? [0.2, 0.8, 0.1] : [0, -0.6, 0.2]), {
  root: [0, 0, 0, 0, -0.01, 0.12], body: [0.3, 0, 0], abdomen: [-0.15, 0, 0],
});

const attack = (): Clip<B> =>
  clip<B>('attack', 0.55, false, (b, t) => {
    timeline(b, t, [
      [0, { ...COCK, body: [-0.38, 0, 0] }],
      [0.06, COCK, outQuad],
      [0.13, BITE, inCubic],
      [0.24, { ...BITE, root: [0, 0, 0, 0, 0, 0.08] }, outCubic],
      [0.55, ST, inOut],
    ], { abdomen: 0.03 });
    b.body.rot(0.05 * kick(t, 0.13, 5, 10));
  }, (t) => {
    const k = Math.max(0, kick(t, 0.13, 4, 10));
    return [1 + 0.05 * k, 1 - 0.07 * k, 1 + 0.05 * k];
  });

const hit = (): Clip<B> =>
  clip<B>('hit', 0.3, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 3, 10) * span(t, 0, 0.02, outQuad);
    const a = Math.abs(k);
    b.root.pos(0, 0.02 * a, -0.04 * k).rot(0, 0, 0.1 * k);
    b.body.rot(-0.2 * k);
    b.abdomen.rot(0.2 * kick(t, 0.03, 3, 8));
    for (const i of LEGS) leg(b, i, 0.6 * a, 0, 0.6 * a);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 10));
    return [1 + 0.05 * k, 1 - 0.08 * k, 1 + 0.05 * k];
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 1.5, true, (b, t) => {
    const p = phase(t, 1.5) * TAU;
    apply(b, ST, 0.5);
    b.root.pos(0, -0.02, 0).rot(0, 0.15 * Math.sin(p), 0.08 * Math.cos(p));
    b.body.rot(0.12, 0, 0);
    b.abdomen.rot(-0.1, 0.15 * Math.sin(p + 1), 0);
    for (const i of LEGS) {
      const tw = Math.max(0, Math.sin(p * 3 + i * 1.7)) ** 4;
      leg(b, i, 0.8 * tw, 0.5 * Math.sin(p * 2 + i * 2.1) * tw, 0.6);
    }
  });

/** It jerks up, flips over onto its back with a little bounce and the legs curl in, twitching. */
const die = (): Clip<B> =>
  clip<B>('die', 1.3, false, (b, t) => {
    const curl = (c: number): P => legsPose((i) => [0, 0.3 * (1.5 - row(i)), 0, c]);
    timeline(b, t, [
      [0, ST],
      [0.12, { ...legsPose(() => [1.6, 0, 0]), root: [0, 0, 0, 0, 0.08, 0], body: [-0.3, 0, 0] }, outExpo],
      [0.38, { ...curl(0.5), root: [0, 0, 1.8, -0.06, 0.28, 0] }, inQuad],
      [0.55, { ...curl(0.8), root: [0, 0, Math.PI, 0, 0.4, 0] }, inQuad],
      [0.66, { ...curl(0.85), root: [0, 0, Math.PI - 0.1, 0, 0.42, 0] }, outBack],
      [1.3, { ...curl(1.0), root: [0, 0, Math.PI - 0.05, 0, 0.4, 0] }, inOut],
    ], { abdomen: 0.03 });
    const th = envelope(t, 0.6, 0.7, 0.9, 1.25, outQuad);
    for (const i of LEGS) leg(b, i, 0.5 * Math.sin(TAU * 7 * t + i * 1.9) * th, 0.4 * Math.sin(TAU * 5 * t + i * 2.7) * th);
    b.abdomen.scale(1 - 0.1 * span(t, 0.6, 1.3, inCubic));
  }, (t) => {
    const k = Math.max(0, kick(t, 0.55, 3, 9));
    return [1 + 0.06 * k, 1 - 0.1 * k, 1 + 0.06 * k];
  });

/** Its clips (see models/spiderling.ts for the bones). */
export const spiderlingClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { spiderling: spiderlingClips };
